import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ProjectLocationService from "../services/ProjectLocationService";
import Breadcrumb from "../components/Breadcrumb";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";

export default function ViewProjectLocations() {
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const messageTimer = useRef(null);
  const navigate = useNavigate();

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const closeMessage = () => {
    setMessage("");
    if (messageTimer.current) clearTimeout(messageTimer.current);
  };

  const { permission, can } = usePermission(
    ScreenData.Project_Locations,
    showMessage
  );

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await ProjectLocationService.getAll();
      if (res.status) {
        const formatted = (res.data || []).map((l) => ({
          ...l,
          isActive: l.status === "Active",
        }));
        setLocations(formatted);
      } else {
        showMessage(res.message || "Failed to fetch locations");
      }
    } catch (err) {
      console.error(err);
      showMessage("Error fetching locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleSelect = (id) => {
    setSelected(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    );
  };

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? locations.map((l) => l.id) : []);
  };

  const handleAdd = () => navigate("/cpnl/projectlocation/add");

  const handleModify = () => {
    if (selected.length === 1) {
      const loc = locations.find((l) => l.id === selected[0]);
      localStorage.setItem("editProjectLocation", JSON.stringify(loc));
      navigate("/cpnl/projectlocation/add");
    } else if (!selected.length) showMessage("Select a location to modify");
    else showMessage("Select only one location to modify");
  };

  const handleToggleStatus = async (id, currentStatus) => {
    if (!can("canModify")) {
      showMessage("You don't have permission to modify status", "error");
      return;
    }
    const newStatusText = currentStatus ? "Inactive" : "Active";

    const confirmed = window.confirm(
      `Are you sure you want to Mark this Location as ${newStatusText}?`
    );
    if (!confirmed) return;

    try {
      const res = await ProjectLocationService.updateStatus(id);

      if (res && res.status === false) {
        showMessage(res.message || "Failed to update status", "error");
        return;
      }

      setLocations((prev) =>
        prev.map((l) => (l.id === id ? { ...l, isActive: !currentStatus } : l))
      );

      showMessage(`Location status updated to ${newStatusText}`, "success");
    } catch (error) {
      console.error("Status update failed:", error);
      showMessage("Failed to update status", "error");
    }
  };

  return (
    <>
      <Breadcrumb
        pageTitle="Project Locations"
        secondTitle="Projects"
      ></Breadcrumb>
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {message && (
            <div
              className={`alert ${
                msgType === "success" ? "alert-success" : "alert-danger"
              } d-flex justify-content-between align-items-center mb-2 p-2`}
              style={{ borderRadius: "5px" }}
            >
              <span>{message}</span>
              <button className="btn-close" onClick={closeMessage}></button>
            </div>
          )}

          <div className="mb-3 pb-2 border-bottom">
            <button
              className="d-inline-flex btn btn-success me-2"
              onClick={handleAdd}
              disabled={loading}
            >
              <i class="ti ti-plus me-1"></i> Add
            </button>
            <button
              className="d-inline-flex btn btn-warning me-2"
              onClick={handleModify}
              disabled={loading}
            >
              <i class="ti ti-pencil me-1"></i> Modify
            </button>
            {/* <button
              className="btn btn-danger btn-sm me-2"
              onClick={handleDelete}
              disabled={loading}
            >
              Delete
            </button> */}
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selected.length === locations.length &&
                        locations.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  {/* <th>Company</th> */}
                  <th>Project</th>
                  <th>Location</th>
                  <th>From KMS</th>
                  <th>TO KMS</th>
                  <th>Status</th>
                  {/* <th>Action</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      ></div>
                    </td>
                  </tr>
                ) : locations.length > 0 ? (
                  locations.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(l.id)}
                          onChange={() => handleSelect(l.id)}
                        />
                      </td>
                      {/* <td>{l.companyName}</td> */}
                      <td>{l.projectName}</td>
                      <td>{l.locationName}</td>
                      <td> {l.fromKMS} KMs </td>
                      <td> {l.toKMS} KMs </td>
                      <td>
                        <span
                          className={`badge ${
                            l.isActive ? "bg-success" : "bg-secondary"
                          }`}
                          onClick={() => handleToggleStatus(l.id, l.isActive)}
                          style={{ cursor: can("canModify") ? "pointer" : "default" }}
                        >
                          {l.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {/* <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => toggleStatus(l.id)}
                        disabled={l.isAssigned}
                        title={
                          l.isAssigned ? "Location assigned to project" : ""
                        }
                      >
                        {l.status === "Active" ? "Inactivate" : "Activate"}
                      </button>
                    </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      No project locations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
